import {
  Box,
  Button,
  Modal,
  Typography,
  useMediaQuery,
  Zoom,
} from '@mui/material';
import React, { useState } from 'react';
import {
  IconWrapper,
  ImageWrapper,
  IStyles,
  PrimaryButton,
  pxToRem,
  pxTovW,
  ConductionButtonsComponent,
  theme,
  useFeatureEnabled,
  deserify,
} from '@geneo2-web/shared-ui';
import {
  AssessmentEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import {
  getModeOfConductionEnum,
  getModeOfConductionString,
} from '../../../utils/functions';
import { useDispatch } from 'react-redux';
import { setAssessmentCreationType } from '../../Assessment/reducer/assessmentFlow.slice';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';
import { interactionEvent } from '../../Auth/auth.events';
import { Feature } from '@protos/school_management/school.db_pb';

const styles: IStyles = {
  modalBox: {
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'center',
    alignItems: { xs: 'center', md: 'center' },
    height: { xs: '96vh', md: '100vh' },
    width: { xs: '100vw', md: '100vw' },
    mt: { xs: '2vh', md: '0.5%' },
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    boxSizing: 'border-box',
  },
  mainDisplayBox: {
    position: 'relative',
    borderRadius: { xs: pxToRem(10), md: pxTovW(20) },
    background: 'white',
    padding: {
      xs: `${pxToRem(17)} ${pxToRem(20)}`,
      md: `${pxTovW(21)} ${pxTovW(48)}`,
      lg: `${pxTovW(21)} ${pxTovW(48)}`,
    },
    width: { xs: '95%', md: 'max-content' },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(24), md: pxToRem(10) },
    alignItems: 'center',
    boxSizing: 'border-box',
    maxHeight: { xs: '80vh', md: '95vh' },
    // overflowY: 'auto',
  },
};

interface IProps {
  open: boolean;
  selectedValue?: AssessmentEnum;
  optionClickHandler: (assessmentType: AssessmentEnum) => void;
  submitClickHandler: () => void;
  handleClose?: () => void;
  classInfo?: string;
  isClickerEnabled?: boolean;
  selectedMode?: 'Geneo App' | 'Geneo Clicker' | '';
  setSelectedMode?: React.Dispatch<
    React.SetStateAction<'Geneo App' | 'Geneo Clicker' | ''>
  >;
}
interface AssessmentOptions {
  [key: string]: OptionItemValue;
}
export default function AssementSelectionPopup(props: IProps) {
  const { selectedMode, setSelectedMode } = props;
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();

  const { class_subject_info } = useAppSelector((state) => state.homeDashboard);
  const { assesment_creation_type } = useAppSelector(
    (state) => state.assessment
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user_info } = deserify(useAppSelector((state) => state.auth));

  const isQPGFeatureEnabled = useFeatureEnabled(
    user_info?.schoolDetails[0]?.featuresPlanInfo,
    Feature.QUESTION_PAPER_GENERATOR
  );

  const assessmentOptions: AssessmentOptions = {
    [AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT]: {
      typeKey: AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT,
      label: 'Formative Assessment',
      icon: 'checklist',
      phrase: 'Test Proficiency Across Chapter.',
      backgroundColor: '#F2F9FF',
      borderColor: '#007CDC',
      disabled: false,
    },
    [AssessmentEnum.ASSESSMENT_HOMEWORK]: {
      typeKey: AssessmentEnum.ASSESSMENT_HOMEWORK,
      label: 'Homework',
      icon: 'homeschooling',
      phrase: 'Quick Practice for all Topics.',
      backgroundColor: '#F2F9FF',
      borderColor: '#007CDC',
      disabled: false,
    },
    [AssessmentEnum.ASSESSMENT_QUESTION_PAPER]: {
      typeKey: AssessmentEnum.ASSESSMENT_QUESTION_PAPER,
      label: 'Question Paper Generator',
      icon: 'qpg',
      phrase: 'Create Custom Question Papers',
      backgroundColor: '#F2F9FF',
      borderColor: '#007CDC',
      disabled: false,
      hidden: !isQPGFeatureEnabled,
    },
  };
  // const [modeOfConductionValue, setModeOfConductionValue] = useState<string>(
  //   getModeOfConductionString(
  //     ModeOfConductionEnum.MODE_OF_CONDUCTION_GENEO_APP
  //   ) || 'Geneo App'
  // );
  const {
    open,
    selectedValue,
    handleClose,
    optionClickHandler,
    submitClickHandler,
    isClickerEnabled,
  } = props;

  return (
    <Modal open={open} onClose={handleClose}>
      <Zoom in={open} mountOnEnter unmountOnExit timeout={700}>
        <Box sx={styles.modalBox}>
          <Box sx={styles.mainDisplayBox}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: pxToRem(24), md: pxToRem(10) },
                overflowY: 'auto',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography variant="h2">Choose Type of Assessment</Typography>
              {props.classInfo && (
                <Typography variant="bodyText" color={'primary.main'}>
                  {props.classInfo}
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  gap: { xs: pxToRem(12), md: pxToRem(15) },
                  // marginTop: { xs: pxToRem(9), md: pxTovW(12) },
                }}
              >
                {Object.entries(assessmentOptions)
                  .filter(([key, value]) => !value.hidden)
                  .map(([key, value], index) => (
                    <OptionItem
                      key={index}
                      selectedValue={selectedValue}
                      value={value}
                      onClick={async (assessmentType: AssessmentEnum) => {
                        optionClickHandler(assessmentType);
                        await interactionEvent({
                          context: 'Choose_type_of_assessment',
                          name:
                            assessmentType ===
                            AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT
                              ? 'FORMATIVE'
                              : assessmentType ===
                                AssessmentEnum.ASSESSMENT_DIAGNOSTIC_ASSESSMENT
                              ? 'DIAGNOSTIC'
                              : assessmentType ===
                                AssessmentEnum.ASSESSMENT_QUESTION_PAPER
                              ? 'QUESTION_PAPER_GENERATOR'
                              : 'HOMEWORK',
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        });
                      }}
                    />
                  ))}
              </Box>

              {isClickerEnabled &&
                selectedValue &&
                [
                  AssessmentEnum.ASSESSMENT_FORMATIVE_ASSESSMENT,
                  AssessmentEnum.ASSESSMENT_HOMEWORK,
                ].includes(selectedValue) && (
                  <ConductionButtonsComponent
                    value={
                      selectedMode === '' &&
                      class_subject_info?.isClickerSupported
                        ? 'clicker'
                        : selectedMode === 'Geneo Clicker'
                        ? 'clicker'
                        : 'mobile-app'
                    }
                    onChange={async (val: 'mobile-app' | 'clicker') => {
                      const modeString =
                        val === 'mobile-app' ? 'Geneo App' : 'Geneo Clicker';

                      setSelectedMode && setSelectedMode(modeString);

                      if (selectedMode) {
                        await interactionEvent({
                          context: 'Mode_of_Conduction',
                          name: selectedMode.toUpperCase().replace(/\s+/g, '_'),
                          pathSegments: pathSegments,
                          isOffline: isOffline,
                        });
                      }
                    }}
                  />
                )}
              <Box
                sx={{
                  width: { xs: pxToRem(230), md: pxTovW(650), lg: pxTovW(497) },
                }}
              >
                <PrimaryButton
                  fullWidth
                  onClick={() => {
                    if (isClickerEnabled) {
                      if (selectedMode) {
                        dispatch(
                          setAssessmentCreationType({
                            ...assesment_creation_type,
                            modeOfConduction:
                              getModeOfConductionEnum(selectedMode),
                          })
                        );
                      }
                    }
                    submitClickHandler();
                  }}
                  sx={{
                    boxShadow: 'none',
                  }}
                  disabled={selectedValue ? false : true}
                >
                  <Typography variant="h2" color="common.white">
                    PROCEED
                  </Typography>
                </PrimaryButton>
              </Box>
              {!isMobile ? (
                <Button
                  onClick={handleClose}
                  sx={{
                    p: '0px',
                    position: { md: 'absolute' },
                    top: 0,
                    right: `-${pxTovW(65)}`,
                  }}
                >
                  <IconWrapper
                    name="close-yellow"
                    size="md"
                    parentFolder="icons"
                    type="png"
                    customSx={{
                      width: { xs: pxToRem(33), md: pxTovW(54) },
                      height: { xs: pxToRem(33), md: pxTovW(54) },
                    }}
                  />
                </Button>
              ) : null}
            </Box>
          </Box>

          {isMobile ? (
            <Button onClick={handleClose} sx={{ p: '0px' }}>
              <IconWrapper
                name="close-yellow"
                size="md"
                parentFolder="icons"
                type="png"
                customSx={{
                  width: { xs: pxToRem(33), md: pxTovW(54) },
                  height: { xs: pxToRem(33), md: pxTovW(54) },
                }}
              />
            </Button>
          ) : null}
        </Box>
      </Zoom>
    </Modal>
  );
}

interface OptionItemValue {
  icon: string;
  label: string;
  phrase: string;
  typeKey: AssessmentEnum;
  backgroundColor?: string;
  borderColor?: string;
  disabled?: boolean;
  hidden?: boolean;
}
interface OptionItemProps {
  value: OptionItemValue;
  onClick?: (assessmentKey: AssessmentEnum) => void;
  key: number;
  selectedValue?: AssessmentEnum;
  classInfoDisplay?: string;
}

const OptionItem: React.FC<OptionItemProps> = ({
  key,
  value,
  onClick,
  selectedValue,
  classInfoDisplay,
}) => {
  return (
    <Box
      key={key}
      className="option"
      onClick={() => {
        if (value.disabled === false && onClick) {
          if (value.typeKey) {
            onClick(value.typeKey);
          }
        }
      }}
      sx={{
        cursor: value.disabled ? 'not-allowed' : 'pointer',
        // width: '100%',
        display: 'flex',
        gap: { xs: pxToRem(10), md: pxToRem(15) },
        padding: {
          xs: `${pxToRem(12)} ${pxToRem(9)}`,
          md: `${pxTovW(20)} ${pxTovW(20)}`,
        },
        borderRadius: '10px',
        opacity: value.disabled ? 0.5 : 1,
        backgroundColor: value.backgroundColor
          ? value.backgroundColor
          : 'white',
        border: `1px solid ${value.borderColor ? value.borderColor : 'black'}`,
        // '&:hover': {
        //   boxShadow: value.disabled ? 'none' : '0 2px 5px rgba(0, 0, 0, 1)',
        // },
        boxShadow:
          selectedValue === value.typeKey
            ? '0 3px 3px rgba(0, 0, 0, 1)'
            : 'none',
      }}
    >
      <ImageWrapper
        name={value.icon}
        type="png"
        parentFolder="icons"
        styles={{
          width: { xs: pxToRem(34), md: pxTovW(83) },
          height: 'auto',
          marginY: 'auto',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: { xs: pxToRem(7), md: pxTovW(8) },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: pxToRem(16), md: pxTovW(33) },
            fontWeight: 600,
            fontFamily: 'poppins',
          }}
        >
          {value.label}
        </Typography>
        <Typography
          variant="bodyText"
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordWrap: 'break-word',
            maxWidth: { xs: pxToRem(200), md: pxTovW(450), lg: pxTovW(452) },
            fontSize: { xs: pxToRem(14), md: pxTovW(28) },
          }}
        >
          {value.phrase}
        </Typography>
        <Typography variant="bodyText" color={'primary.main'}>
          {classInfoDisplay}
        </Typography>
      </Box>
    </Box>
  );
};
